import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { DistributionChart } from '@/components/application/charts/distribution-chart';
import type { StatsRecapResponse } from '@/types/api/containers-recap';

export interface DistributionDataItem {
    label: string;
    value: number;
    color: string;
}

export interface DistributionData {
    riskData: DistributionDataItem[];
    statusData: DistributionDataItem[];
    documentData: DistributionDataItem[];
    total: number;
    documentTotal: number;
}

interface DistributionChartsSectionProps {
    /** Distribution data for the three charts */
    distributionData: DistributionData;
    /** Stats data from API for granular issue counts */
    statsData?: StatsRecapResponse | null;
    /** Start date for filtering (ISO format, optional) */
    startDate?: string | null;
    /** End date for filtering (ISO format, optional) */
    endDate?: string | null;
    /** Layout mode: horizontal grid or vertical stack */
    layout?: 'grid' | 'stack';
}

export function DistributionChartsSection({
    distributionData,
    statsData,
    startDate,
    endDate,
    layout = 'grid',
}: DistributionChartsSectionProps) {
    const { t } = useTranslation(['pages:containers']);
    const wrapperClassName = layout === 'stack'
        ? 'grid grid-cols-1 gap-4'
        : 'grid grid-cols-1 gap-6 lg:grid-cols-3';

    // Build date query params to pass context to issues page
    const dateParams = [
        startDate ? `start_date=${startDate}` : '',
        endDate ? `end_date=${endDate}` : '',
    ].filter(Boolean).join('&');
    const dateSuffix = dateParams ? `&${dateParams}` : '';

    return (
        <div className={wrapperClassName}>
            <DistributionChart
                title={t('list.charts.riskLevels', { ns: 'pages:containers' }) || 'Risk Level'}
                data={distributionData.riskData}
                total={distributionData.total}
            />
            <DistributionChart
                title={t('list.charts.containerStatus', { ns: 'pages:containers' }) || 'Container Status'}
                data={distributionData.statusData}
                total={distributionData.total}
            />
            {/* Document Issues Recap - replaces the old Document Parsing Status chart */}
            <div className="rounded-xl bg-primary p-4 ring-1 ring-secondary">
                <h3 className="text-sm font-semibold text-primary mb-3">
                    {t('list.charts.documentStatus', { ns: 'pages:containers' }) || 'Document Issues Recap'}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                    <Link
                        to={`/shipper/issues?issue_type=missing_document&category=document${dateSuffix}`}
                        className="flex items-center gap-2 p-2 rounded-lg bg-gray-100 dark:bg-gray-800/40 hover:ring-1 hover:ring-gray-300 dark:hover:ring-gray-600 transition-shadow"
                    >
                        <span className="text-lg font-bold text-gray-700 dark:text-gray-300">{statsData?.missing_document_count ?? 0}</span>
                        <span className="text-xs text-gray-600 dark:text-gray-400">Missing Docs</span>
                    </Link>
                    <Link
                        to={`/shipper/issues?issue_type=single_document_check&category=document${dateSuffix}`}
                        className="flex items-center gap-2 p-2 rounded-lg bg-brand-50 dark:bg-brand-950/30 hover:ring-1 hover:ring-brand-300 dark:hover:ring-brand-600 transition-shadow"
                    >
                        <span className="text-lg font-bold text-brand-700 dark:text-brand-400">{statsData?.single_check_issue_count ?? 0}</span>
                        <span className="text-xs text-brand-600 dark:text-brand-500">Single Check</span>
                    </Link>
                    <Link
                        to={`/shipper/issues?issue_type=cross_check_document_check&category=document${dateSuffix}`}
                        className="flex items-center gap-2 p-2 rounded-lg bg-purple-50 dark:bg-purple-950/30 hover:ring-1 hover:ring-purple-300 dark:hover:ring-purple-600 transition-shadow"
                    >
                        <span className="text-lg font-bold text-purple-700 dark:text-purple-400">{statsData?.cross_check_issue_count ?? 0}</span>
                        <span className="text-xs text-purple-600 dark:text-purple-500">Cross-Check</span>
                    </Link>
                    <Link
                        to={`/shipper/issues?category=non_document${dateSuffix}`}
                        className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 hover:ring-1 hover:ring-blue-300 dark:hover:ring-blue-600 transition-shadow"
                    >
                        <span className="text-lg font-bold text-blue-700 dark:text-blue-400">{statsData?.non_doc_issue_count ?? 0}</span>
                        <span className="text-xs text-blue-600 dark:text-blue-500">Other Issues</span>
                    </Link>
                </div>
                {/* Total summary row */}
                <Link
                    to={`/shipper/issues${dateSuffix ? `?${dateParams}` : ''}`}
                    className="mt-3 pt-3 border-t border-secondary flex items-center justify-between hover:opacity-80 transition-opacity"
                >
                    <span className="text-xs text-tertiary">Total active issues</span>
                    <span className="text-sm font-semibold text-primary">{statsData?.total_issue_count ?? 0}</span>
                </Link>
            </div>
        </div>
    );
}
